// @oasis-permission-inherits: /jerboa-client-preview (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import {
  formatCount,
  formatDurationMs,
  formatFps,
  formatFrameMs,
  formatMb,
  formatTimestamp,
  playbackQualityFromBuckets,
  qualityBadgeClass,
  statusVariant,
} from "~/modules/jerboa-client-preview/formatters";
import { MemoryHeatmap } from "~/modules/jerboa-client-preview/memory-heatmap";
import { PlaybackHeatmap } from "~/modules/jerboa-client-preview/playback-heatmap";
import { getJerboaClientPreviewQuery } from "~/modules/jerboa-client-preview/queries";
import {
  jerboaClientPreviewSourcePlatformSchema,
  jerboaClientPreviewStatusSchema,
} from "~/modules/jerboa-client-preview/schemas";
import { JerboaUserEnvCard } from "~/modules/jerboa/components/user-env-card";

export const Route = createFileRoute("/_dash/jerboa-client-preview/$id")({
  validateSearch: z.object({
    page: z.number().optional(),
    status: jerboaClientPreviewStatusSchema.optional(),
    projectId: z.string().optional(),
    sourcePlatform: jerboaClientPreviewSourcePlatformSchema.optional(),
    metricDays: z.union([z.literal(1), z.literal(7), z.literal(30)]).optional(),
  }),
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getJerboaClientPreviewQuery.queryOptions({
        pathParams: { id: params.id },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const { page, status, projectId, sourcePlatform, metricDays } = Route.useSearch();
  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-preview"
        search={{ page, status, projectId, sourcePlatform, metricDays }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Playback Results
      </Link>
      <Skeleton className="h-10 w-96 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {hint ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A real button, not the bare SVG: the icon is not keyboard-
                  focusable, and this tooltip is the metric's only caveat. */}
              <button type="button" aria-label={hint} className="cursor-help">
                <Info className="size-3 shrink-0" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{hint}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="mt-1 text-sm font-medium">{value ?? "-"}</div>
    </div>
  );
}

const TARGET_FPS_OPTIONS = [60, 30, 24] as const;

/**
 * Format the project-time range covered by a play→stop cycle —
 * shows up next to the heatmap title so an operator immediately
 * knows which slice of the project the row covers (e.g.
 * "0:12.4 → 0:18.7"). Returns `null` when the doc doesn't carry
 * the range (legacy session before the field was added).
 */
function formatProjectRange(startMs: number | null, endMs: number | null): string | null {
  if (startMs == null || endMs == null) {
    return null;
  }
  return `${formatProjectTime(startMs / 1000)} → ${formatProjectTime(endMs / 1000)}`;
}

function formatProjectTime(secs: number): string {
  const rounded = Math.round(secs * 10) / 10;
  const mins = Math.floor(rounded / 60);
  const rem = rounded - mins * 60;
  return `${mins}:${rem.toFixed(1).padStart(4, "0")}`;
}

/** `init_duration_ms` carries `wasmInit + resourceLoad`; subtract WASM
 *  to surface asset/font load time on its own. Returns `null` when
 *  either side is missing so `formatDurationMs` renders `-`. */
function computeAssetLoadDurationMs(
  initDurationMs: number | null | undefined,
  wasmLoadDurationMs: number | null | undefined
): number | null {
  if (initDurationMs == null || wasmLoadDurationMs == null) {
    return null;
  }
  return Math.max(0, initDurationMs - wasmLoadDurationMs);
}

function PlaybackHeatmapCard({
  fpsBuckets,
  bucketWindowMs,
  p90PlaybackFps,
  docTargetFps,
  projectStartMs,
  projectEndMs,
  wasmMemoryBuckets,
  jsHeapBuckets,
  wasmLiveBuckets,
  nativePhysicalFootprintBuckets,
  sourcePlatform,
}: {
  fpsBuckets: number[] | null | undefined;
  bucketWindowMs: number | null | undefined;
  p90PlaybackFps: number | null;
  docTargetFps: number | null;
  projectStartMs: number | null;
  projectEndMs: number | null;
  wasmMemoryBuckets: number[] | null | undefined;
  jsHeapBuckets: number[] | null | undefined;
  wasmLiveBuckets: number[] | null | undefined;
  nativePhysicalFootprintBuckets: number[] | null | undefined;
  sourcePlatform: "web" | "ios";
}) {
  // Default to the target FPS persisted on the camel doc (display refresh
  // rate captured at session time, or `--fps` for native repros). Let the
  // operator override to compare against alternative target rates.
  const [targetFps, setTargetFps] = useState<number>(() => {
    if (docTargetFps && docTargetFps > 0) {
      return docTargetFps;
    }
    const guess = fpsBuckets?.[0];
    if (guess && guess >= 45) {
      return 60;
    }
    if (guess && guess >= 22) {
      return 30;
    }
    return 24;
  });
  const buckets = fpsBuckets ?? [];
  const hasData = buckets.length > 0;
  const quality = playbackQualityFromBuckets(buckets, targetFps);
  const projectRangeLabel = formatProjectRange(projectStartMs, projectEndMs);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <span>Playback Heatmap</span>
            {projectRangeLabel && (
              <span className="font-mono text-xs font-normal text-muted-foreground">
                {projectRangeLabel}
              </span>
            )}
          </div>
          <Badge variant="outline" className={cn("text-xs uppercase", qualityBadgeClass(quality))}>
            {quality === "unknown" ? "no data" : quality}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasData ? (
          <PlaybackHeatmap
            fpsBuckets={buckets}
            bucketWindowMs={bucketWindowMs ?? 1000}
            targetFps={targetFps}
            projectStartSecs={projectStartMs != null ? projectStartMs / 1000 : null}
          />
        ) : (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No FPS samples captured for this session — heatmap requires the JRB-677 collector.
            Sessions written before the collector rolled out will show this empty state.
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Target FPS:</span>
          {TARGET_FPS_OPTIONS.map((fps) => (
            <button
              key={fps}
              type="button"
              onClick={() => setTargetFps(fps)}
              className="inline-flex"
            >
              <Badge variant={targetFps === fps ? "default" : "outline"}>{fps}</Badge>
            </button>
          ))}
          <span className="ml-auto">P90 FPS: {p90PlaybackFps?.toFixed(1) ?? "-"}</span>
        </div>
        {nativePhysicalFootprintBuckets && nativePhysicalFootprintBuckets.length > 0 && (
          <MemoryHeatmap
            samples={nativePhysicalFootprintBuckets}
            bucketWindowMs={bucketWindowMs ?? 1000}
            label="Native physical footprint"
            projectStartSecs={projectStartMs != null ? projectStartMs / 1000 : null}
          />
        )}
        {sourcePlatform === "web" && wasmMemoryBuckets && wasmMemoryBuckets.length > 0 && (
          <MemoryHeatmap
            samples={wasmMemoryBuckets}
            bucketWindowMs={bucketWindowMs ?? 1000}
            label="WASM linear memory (reserved)"
            projectStartSecs={projectStartMs != null ? projectStartMs / 1000 : null}
          />
        )}
        {wasmLiveBuckets && wasmLiveBuckets.length > 0 && (
          <MemoryHeatmap
            samples={wasmLiveBuckets}
            bucketWindowMs={bucketWindowMs ?? 1000}
            label="WASM allocator live bytes"
            projectStartSecs={projectStartMs != null ? projectStartMs / 1000 : null}
          />
        )}
        {jsHeapBuckets && jsHeapBuckets.length > 0 && (
          <MemoryHeatmap
            samples={jsHeapBuckets}
            bucketWindowMs={bucketWindowMs ?? 1000}
            label="JS heap"
            projectStartSecs={projectStartMs != null ? projectStartMs / 1000 : null}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Per-bucket breakdown table — mirrors `native_viewer analyze --mode
 * frames` so the same data shape can be inspected here when the
 * heatmap row is too coarse and you need a concrete timestamp to
 * reproduce. JRB-677.
 */
function PlaybackBucketBreakdownCard({
  fpsBuckets,
  bucketWindowMs,
  targetFps,
  projectStartMs,
}: {
  fpsBuckets: number[];
  bucketWindowMs: number;
  targetFps: number | null;
  projectStartMs: number | null;
}) {
  const effectiveTarget = targetFps && targetFps > 0 ? targetFps : 60;
  const bucketSecs = bucketWindowMs / 1000;
  const baseSecs = projectStartMs != null ? projectStartMs / 1000 : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Per-bucket breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-muted-foreground">
          {fpsBuckets.length} × {bucketWindowMs}ms windows · target {effectiveTarget} fps
        </div>
        <div className="mt-3 max-h-96 overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Window</th>
                <th className="px-3 py-2 text-right font-medium">FPS</th>
                <th className="px-3 py-2 text-right font-medium">Ratio</th>
                <th className="px-3 py-2 text-left font-medium">Quality</th>
              </tr>
            </thead>
            <tbody>
              {fpsBuckets.map((fps, idx) => {
                const start = baseSecs + idx * bucketSecs;
                const end = start + bucketSecs;
                const ratio = Math.min(1, fps / effectiveTarget);
                let quality: "green" | "yellow" | "red";
                if (ratio >= 0.9) {
                  quality = "green";
                } else if (ratio >= 0.5) {
                  quality = "yellow";
                } else {
                  quality = "red";
                }
                return (
                  <tr key={idx} className="border-t">
                    <td className="px-3 py-1.5 text-muted-foreground">{idx}</td>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">
                      {start.toFixed(2)}s – {end.toFixed(2)}s
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fps.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{ratio.toFixed(2)}</td>
                    <td className="px-3 py-1.5">
                      <Badge
                        variant="outline"
                        className={cn("text-[10px] uppercase", qualityBadgeClass(quality))}
                      >
                        {quality}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { page, status, projectId, sourcePlatform, metricDays } = Route.useSearch();

  const { data } = getJerboaClientPreviewQuery.useSuspenseQuery({
    pathParams: { id },
  });

  const result = data.result;

  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-preview"
        search={{ page, status, projectId, sourcePlatform, metricDays }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Playback Results
      </Link>

      <JerboaUserEnvCard
        id={result.id}
        captionsProjectId={result.captionsProjectId}
        projectProtoUri={result.projectProtoUri}
        sessionId={result.sessionId}
        userId={result.userId}
        orgId={result.orgId}
        browserInfo={result.browserInfo}
        appVersion={result.appVersion}
        jerboaVersion={result.jerboaVersion}
      />

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Trigger:</span>
            <Badge variant="outline">{result.trigger}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Platform:</span>
            <Badge variant="outline">{result.sourcePlatform}</Badge>
          </div>
          {result.editType && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit Type:</span>
              <Badge variant="outline">{result.editType}</Badge>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Created At:</span>
            <span className="text-sm">{formatTimestamp(result.createdAt)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Aspect Ratio" value={result.aspectRatio} />
            <MetricCard label="Width" value={result.width} />
            <MetricCard label="Height" value={result.height} />
            <MetricCard label="Frames Rendered" value={result.framesRendered} />
            <MetricCard
              label="Session Duration"
              value={formatDurationMs(result.sessionDurationMs)}
            />
            <MetricCard label="WASM Load" value={formatDurationMs(result.wasmLoadDurationMs)} />
            <MetricCard
              label="Asset Load"
              value={formatDurationMs(
                computeAssetLoadDurationMs(result.initDurationMs, result.wasmLoadDurationMs)
              )}
            />
            <MetricCard label="Total Errors" value={result.totalErrors} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Playback Performance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Avg Playback FPS" value={formatFps(result.avgPlaybackFps)} />
            <MetricCard label="P50 Playback FPS" value={formatFps(result.p50PlaybackFps)} />
            <MetricCard label="P90 Playback FPS" value={formatFps(result.p90PlaybackFps)} />
            <MetricCard label="P99 Playback FPS" value={formatFps(result.p99PlaybackFps)} />
            <MetricCard
              label="Playing Duration"
              value={formatDurationMs(result.playingDurationMs)}
            />
            <MetricCard
              label="Suspended"
              value={formatDurationMs(result.suspendedMs)}
              hint="Hidden-tab or sleep time the engine excluded from Playing Duration and the frame-gap stats."
            />
            <MetricCard label="Suspend Stretches" value={formatCount(result.suspendCount)} />
            <MetricCard
              label="Inferred Suspends"
              value={formatCount(result.inferredSuspendCount)}
              hint="Deduced from a gap of five seconds or more rather than reported by the page; a genuine multi-second freeze lands here too."
            />
            <MetricCard label="Avg Frame Render" value={formatFrameMs(result.avgFrameRenderMs)} />
            <MetricCard label="P50 Frame Render" value={formatFrameMs(result.p50FrameRenderMs)} />
            <MetricCard label="P90 Frame Render" value={formatFrameMs(result.p90FrameRenderMs)} />
            <MetricCard label="P99 Frame Render" value={formatFrameMs(result.p99FrameRenderMs)} />
            <MetricCard label="Max Frame Render" value={formatFrameMs(result.maxFrameRenderMs)} />
            <MetricCard label="Slow Frames (>16.67ms)" value={result.slowFrameCount ?? "-"} />
            <MetricCard label="Longest Frame Gap" value={formatFrameMs(result.longestFrameGapMs)} />
            <MetricCard label="Decoder Errors" value={result.decoderErrorCount ?? "-"} />
            <MetricCard label="Starved Total" value={formatDurationMs(result.starvedTotalMs)} />
            <MetricCard
              label="Longest Starved Stretch"
              value={formatDurationMs(result.starvedLongestMs)}
            />
            <MetricCard label="Forced Renders" value={result.forcedRenderCount ?? "-"} />
            <MetricCard
              label="Stalled-on-Decode Frames"
              value={result.stalledOnDecodeCount ?? "-"}
              hint="Counts only frames that were rendered while reusing the previous decode. Under real starvation rendering is skipped entirely, so this cannot see that stall — use Starved Total instead."
            />
          </div>
        </CardContent>
      </Card>

      <PlaybackHeatmapCard
        fpsBuckets={result.fpsBuckets ?? null}
        bucketWindowMs={result.bucketWindowMs ?? null}
        p90PlaybackFps={result.p90PlaybackFps ?? null}
        docTargetFps={result.targetFps ?? null}
        projectStartMs={result.playbackStartProjectMs ?? null}
        projectEndMs={result.playbackEndProjectMs ?? null}
        wasmMemoryBuckets={result.wasmMemoryBuckets ?? null}
        jsHeapBuckets={result.jsHeapBuckets ?? null}
        wasmLiveBuckets={result.wasmLiveBuckets ?? null}
        nativePhysicalFootprintBuckets={result.nativePhysicalFootprintBuckets ?? null}
        sourcePlatform={result.sourcePlatform}
      />

      {result.fpsBuckets && result.fpsBuckets.length > 0 && (
        <PlaybackBucketBreakdownCard
          fpsBuckets={result.fpsBuckets}
          bucketWindowMs={result.bucketWindowMs ?? 1000}
          targetFps={result.targetFps ?? null}
          projectStartMs={result.playbackStartProjectMs ?? null}
        />
      )}

      {(result.peakWasmMemoryMb != null ||
        result.peakJsHeapMb != null ||
        result.peakNativePhysicalFootprintMb != null) && (
        <Card>
          <CardHeader>
            <CardTitle>Memory</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {result.sourcePlatform === "ios" ? (
                <>
                  <MetricCard
                    label="Peak Physical Footprint"
                    value={formatMb(result.peakNativePhysicalFootprintMb)}
                  />
                  <MetricCard
                    label="Avg Physical Footprint"
                    value={formatMb(result.avgNativePhysicalFootprintMb)}
                  />
                </>
              ) : (
                <>
                  <MetricCard label="Peak WASM" value={formatMb(result.peakWasmMemoryMb)} />
                  <MetricCard label="Avg WASM" value={formatMb(result.avgWasmMemoryMb)} />
                  <MetricCard label="Peak JS Heap" value={formatMb(result.peakJsHeapMb)} />
                  <MetricCard label="Avg JS Heap" value={formatMb(result.avgJsHeapMb)} />
                </>
              )}
            </div>
            {result.sourcePlatform === "web" && result.peakJsHeapMb == null && (
              <p className="text-xs text-muted-foreground">
                JS heap not available — `performance.memory` is Chromium-only.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {result.errorsByCategory && Object.keys(result.errorsByCategory).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Errors by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {Object.entries(result.errorsByCategory).map(([category, count]) => (
                <MetricCard key={category} label={category} value={count} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {result.sampleErrorMessages && result.sampleErrorMessages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Sample Error Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.sampleErrorMessages.map((message, i) => (
                <pre
                  key={i}
                  className="overflow-auto whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {message}
                </pre>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {result.assetUrlMap && Object.keys(result.assetUrlMap).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Asset URL Map</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(result.assetUrlMap, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {result.events.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Analytics Events ({result.events.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {result.events.map((event, i) => (
                <div key={i} className="rounded-md border p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {event.source}
                    </Badge>
                    <code className="text-sm">{event.name}</code>
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-xs">
                    {JSON.stringify(event.properties, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
